/**
 * Product card reveal: slide-in + fade-in as each card scrolls into view.
 *
 * Progressive enhancement — cards are only hidden once this script "arms" them
 * (adds .js-pc-armed). If JS is off, IntersectionObserver is missing, the user
 * prefers reduced motion, or we're in the theme editor, cards stay fully visible.
 *
 * The threshold is intentionally high (35% of the card visible) so the slide/fade
 * plays while the card is clearly in the viewport and the motion is well noticeable.
 */
(() => {
  const SELECTOR = '.product-card.pc-reveal';
  const THRESHOLD = 0.35;

  const prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // In the theme editor, or without IntersectionObserver / with reduced motion,
  // leave every card visible (never arm the hidden state).
  if (
    prefersReduced ||
    !('IntersectionObserver' in window) ||
    (window.Shopify && window.Shopify.designMode)
  ) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= THRESHOLD) {
          entry.target.classList.add('pc-in');
          observer.unobserve(entry.target);
        }
      }
    },
    {
      threshold: [0, THRESHOLD, 0.6],
      rootMargin: '0px 0px -8% 0px',
    }
  );

  const inViewport = (el) => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
  };

  /**
   * Arm (hide + observe) cards that haven't been initialised yet.
   *
   * @param {ParentNode} root
   * @param {boolean} [settle] When true (filter/pagination/section swaps), cards
   *   that are already on-screen are revealed immediately instead of being hidden
   *   and waiting for the observer. Re-rendering the grid (e.g. applying a filter)
   *   strips the JS-added reveal classes via morph, so without this every visible
   *   card would flash to opacity 0 and fade back in on each update.
   */
  const arm = (root, settle = false) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(`${SELECTOR}:not([data-pc-init])`).forEach((el) => {
      el.dataset.pcInit = '1';
      el.classList.add('js-pc-armed');
      if (settle && inViewport(el)) {
        // Already visible after an in-place swap — reveal without a fade flash.
        el.classList.add('pc-in');
      } else {
        observer.observe(el);
      }
    });
  };

  const start = () => arm(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Catch cards added later (pagination, filters, quick add, recommendations).
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        arm(document, true);
        break;
      }
    }
  });
  mutationObserver.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('shopify:section:load', (event) => {
    arm(event.target instanceof Element ? event.target : document, true);
  });
})();
